// import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(ScrollTrigger, SplitText);

'use strict';

const loader = document.querySelector('.loader');
let loaderHidden = false;

const hideLoader = () => {
	if (loader && !loaderHidden) {
		loaderHidden = true;
		loader.classList.add('is-hide');
		setTimeout(() => loader.remove(), 500);
	}
};

import headerScroll from './scripts/headerScroll.js';
import mobileMenuHandler from './scripts/mobileMenuHandler.js';
import counterAnimation from './scripts/counterAnimation.js';
import faqHandler from './scripts/faqHandler.js';
import carouselHandler from './scripts/carouselHandler.js';
import branchesHandler from './scripts/branchesHandler.js';
import videoHandler from './scripts/videoHandler.js';
import ctaHover from './scripts/ctaHover.js';
import partnersMarquee from './scripts/partnersMarquee.js';

const animationsHandler = () => {
	const animateBounceElements = () => {
		const bounceElements = document.querySelectorAll('.js-bounce-anim');
		if (!bounceElements.length) return;

		let mm = gsap.matchMedia();
		mm.add('(min-width: 992px', () => {
			bounceElements.forEach((be) => {
				gsap.from(be, {
					autoAlpha: 0,
					yPercent: 200,
					delay: 0.3,
					duration: 1,
					ease: 'back.out(1)',
				});
			});
		});

		mm.add('(max-width: 991px', () => {
			bounceElements.forEach((be) => {
				gsap.from(be, {
					autoAlpha: 0,
					yPercent: 10,
					delay: 0.3,
					duration: 1,
					ease: 'back.out(1)',
				});
			});
		});
	};

	const animateMoveElements = () => {
		const moveElements = document.querySelectorAll('.js-move-animate');
		if (!moveElements.length) return;

		let mm = gsap.matchMedia();
		mm.add('(min-width: 992px', () => {
			moveElements.forEach((me) => {
				gsap.set(me, {
					yPercent: -2,
					force3D: true,
					willChange: 'transform',
				});

				gsap.to(me, {
					yPercent: 5,
					ease: 'none',
					scrollTrigger: {
						trigger: me,
						scrub: true,
						fastScrollEnd: true,
						preventOverlaps: true,
					},
				});
			});
		});
	};

	const animateTitlesElements = () => {
		const titlesElements = document.querySelectorAll('.js-title-anim');
		if (!titlesElements.length) return;

		const splits = Array.from(titlesElements).map((title) => {
			const s = new SplitText(title, { type: 'words, chars' });
			gsap.set(s.chars, {
				opacity: 0,
				y: 20,
				willChange: 'transform, opacity',
				force3D: true,
			});
			return { el: title, split: s };
		});

		ScrollTrigger.batch(titlesElements, {
			onEnter: (batch) => {
				batch.forEach((title) => {
					const target = splits.find((s) => s.el === title);
					if (target) {
						gsap.to(target.split.chars, {
							opacity: 1,
							y: 0,
							duration: 0.6,
							ease: 'power3.out',
							stagger: 0.02,
							delay: 0.2,
							overwrite: true,
						});
					}
				});
			},
			start: 'top 95%',
			once: true,
		});
	};

	const animationScrollingElements = (
		selector = '.js-scroll',
		options = {}
	) => {
		const scrollingEls = document.querySelectorAll(selector);
		if (!scrollingEls.length) return;

		const defaults = {
			opacity: 0,
			y: 50,
			x: 0,
			scale: 1,
			duration: 0.8,
			ease: 'power3.out',
			stagger: 0.1,
			start: 'top 100%',
			end: 'bottom 80%',
		};

		const settings = { ...defaults, ...options };

		scrollingEls.forEach((el) => {
			gsap.from(el, {
				opacity: settings.opacity,
				y: settings.y,
				x: settings.x,
				scale: settings.scale,
				duration: settings.duration,
				ease: settings.ease,
				stagger: settings.stagger,
				scrollTrigger: {
					trigger: el,
					start: settings.start,
					end: settings.end,
					toggleActions: 'play none none none',
					once: true,
				},
			});
		});
	};

	const animateCirclesSequence = () => {
		const section = document.querySelector('.circles__box');
		if (!section) return;

		gsap.to('.circles__bg', {
			rotation: 2,
			skewX: 10,
			skewY: 10,
			duration: 8,
			repeat: -1,
			yoyo: true,
			ease: 'sine.inOut',
		});

		const items = section.querySelectorAll('.circles__item:not(.main-el)');
		const mainItem = section.querySelector('.main-el');
		const lines = section.querySelectorAll('.circles__line');
		const title = section.querySelector('.circles__title');

		if (!items.length || !mainItem || !title) return;

		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: section,
				start: 'top-=100 top',
				end: `+=${items.length * 300}`,
				scrub: true,
				pin: true,
				pinSpacing: true,
				invalidateOnRefresh: true
			},
		});

		items.forEach((item, index) => {
			tl.to(
				item,
				{
					x: () =>
						mainItem.getBoundingClientRect().left -
						item.getBoundingClientRect().left,
					y: () =>
						mainItem.getBoundingClientRect().top -
						item.getBoundingClientRect().top,
					ease: 'power3.out',
					duration: 1,
				},
				'+=0.2'
			);

			tl.to(
				item,
				{
					opacity: 0,
					duration: 0.2,
					ease: 'power1.out',
				},
				'+=0.5'
			);

			tl.to(
				mainItem,
				{
					scale: 1 + (index + 1) * 0.45,
					ease: 'power2.out',
					duration: 1,
				},
				'<'
			);

			if (lines[index]) {
				tl.to(
					lines[index],
					{
						scale: 0,
						opacity: 0,
						transformOrigin: 'center center',
						ease: 'power2.out',
						duration: 1,
					},
					'<'
				);
			}
		});

		if (title) {
			const s = new SplitText(title, { type: 'words, chars' });
			gsap.set(s.chars, {
				opacity: 0,
				y: 20,
				willChange: 'transform, opacity',
				force3D: true,
			});

			tl.to(s.chars, {
				opacity: 1,
				y: 0,
				duration: 1,
				ease: 'power3.out',
				stagger: 0.03,
			});
		}
	};

	// const lenis = new Lenis({ autoRaf: false });
	// lenis.on('scroll', ScrollTrigger.update);
	// gsap.ticker.add((time) => lenis.raf(time * 1000));
	// gsap.ticker.lagSmoothing(0);

	const positionCircleLines = () => {
		const box = document.querySelector('.circles__box');
		if (!box) return;

		const mainEl = box.querySelector('.circles__item.main-el');
		if (!mainEl) return;

		// Mapping: line class -> corresponding item class
		const lineMap = [
			{ line: '.circles__line.frst-el', item: '.circles__item.frst-el' },
			{ line: '.circles__line.scnd-el', item: '.circles__item.thrd-el' },
			{ line: '.circles__line.thrd-el', item: '.circles__item.frth-el' },
		];

		const update = () => {
			const boxRect = box.getBoundingClientRect();

			// Main element center (relative to box)
			const mainRect = mainEl.getBoundingClientRect();
			const mainCX = mainRect.left + mainRect.width / 2 - boxRect.left;
			const mainCY = mainRect.top + mainRect.height / 2 - boxRect.top;
			const mainRadius = mainRect.width / 2;

			lineMap.forEach(({ line: lineSel, item: itemSel }) => {
				const lineEl = box.querySelector(lineSel);
				const itemEl = box.querySelector(itemSel);
				if (!lineEl || !itemEl) return;

				const itemRect = itemEl.getBoundingClientRect();
				const itemCX = itemRect.left + itemRect.width / 2 - boxRect.left;
				const itemCY = itemRect.top + itemRect.height / 2 - boxRect.top;
				const itemRadius = itemRect.width / 2;

				// Angle from item center to main center
				const dx = mainCX - itemCX;
				const dy = mainCY - itemCY;
				const angle = Math.atan2(dy, dx);
				const dist = Math.sqrt(dx * dx + dy * dy);

				// Line starts at edge of item circle, ends at edge of main circle
				// with a small gap on each side
				const gap = window.innerWidth <= 767 ? 8 : 16;
				const lineLength = dist - itemRadius - mainRadius - gap * 2;
				const startX = itemCX + Math.cos(angle) * (itemRadius + gap);
				const startY = itemCY + Math.sin(angle) * (itemRadius + gap);
				const angleDeg = angle * (180 / Math.PI);

				lineEl.style.left = startX + 'px';
				lineEl.style.top = startY + 'px';
				lineEl.style.width = Math.max(0, lineLength) + 'px';
				lineEl.style.transform = `rotate(${angleDeg}deg)`;
			});
		};

		update();
		window.addEventListener('resize', update);
	};

	positionCircleLines();
	animateCirclesSequence();
	animateTitlesElements();
	animateMoveElements();
	animateBounceElements();
	animationScrollingElements();

	// ScrollTrigger.addEventListener('refresh', () => lenis.resize());

	window.addEventListener('load', () => {
		ScrollTrigger.refresh();
		// lenis.resize();
	});

	// Safety: force Lenis to recalculate after all pin-spacers are created
	// requestAnimationFrame(() => {
	// 	lenis.resize();
	// 	ScrollTrigger.refresh();
	// 	requestAnimationFrame(() => lenis.resize());
	// });
};

document.addEventListener('DOMContentLoaded', () => {
	animationsHandler();
	counterAnimation();
	mobileMenuHandler();
	headerScroll();
	faqHandler();
	carouselHandler();
	branchesHandler();
	videoHandler();
	ctaHover();
	partnersMarquee();

	window.addEventListener('load', hideLoader);
	setTimeout(hideLoader, 7000);
});